import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom';
import { Col, Row, Table, Modal, ModalHeader, ModalBody, Label, Button } from 'reactstrap';
import Skeleton from "react-loading-skeleton";
import { deletePrompt, getPrompt, updatePrompt } from '../../helper/backend-methods';
import RubricPanel from '../../components/Prompt/RubricPanel';
import AskAiToChange from '../../components/Prompt/AskAiToChange';
import VersionHistory from '../../components/Prompt/VersionHistory';
import { showToast } from '../../components/Toast';
import { EmptyRow } from '../../components/EmptyState';
import { useAppContext } from '../../context/AppContext';
import { getDefaultDataCollection, isDefaultDataCollectionKey } from '../../utils/defaultDataCollection';


// Validation constants
const VALIDATION = {
    MIN_LENGTH: 1,
    ERROR_MESSAGES: {
        TITLE: "Please provide valid title",
        FIRST_MESSAGE: "Please provide first message",
        PURPOSE: "Please provide purpose",
        PROMPT: "Please provide a valid prompt",
        CUSTOM_KEY: "Please provide a key",
        CUSTOM_KEY_FORMAT: "Only letters, numbers, and underscores are allowed",
        CUSTOM_KEY_DUPLICATE: "This key is already added",
        CUSTOM_TYPE: "Please select a type",
        CUSTOM_DESCRIPTION: "Please provide a description"
    }
};

const emptyErrors = {
    title: "",
    prompt: "",
    firstMessage: "",
    purpose: "",
    customDataCollection: []
};

const ListPrompts = () => {
    const [loading, setLoading] = useState(false);
    const [prompts, setPrompts] = useState([]);
    const [update, setUpdate] = useState(0);
    const [selectedPrompt, setSelectedPrompt] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [errors, setErrors] = useState({
        title: "",
        prompt: "",
        firstMessage: "",
        purpose: "",
        customDataCollection: []
    });

    const { activeTour } = useAppContext();

    // Memoized fetch function to prevent unnecessary re-renders
    const fetchPrompts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getPrompt();
            if (data?.status) {
                setPrompts(data.data || []);
            } else {
                showToast("error", "Request Failed", data?.message || "Failed to fetch prompts");
            }
        } catch (err) {
            showToast("error", "Request Failed", err.message || "Network error occurred");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPrompts();
    }, [update, fetchPrompts]);

    // Validation helper function
    const validateForm = (promptData) => {
        const newErrors = {};
        let isValid = true;

        if (!promptData.title?.trim() || promptData.title.replace(/\s/g, '').length < VALIDATION.MIN_LENGTH) {
            newErrors.title = VALIDATION.ERROR_MESSAGES.TITLE;
            isValid = false;
        }

        if (!promptData.firstMessage?.trim() || promptData.firstMessage.replace(/\s/g, '').length < VALIDATION.MIN_LENGTH) {
            newErrors.firstMessage = VALIDATION.ERROR_MESSAGES.FIRST_MESSAGE;
            isValid = false;
        }

        if (!promptData.purpose?.trim() || promptData.purpose.replace(/\s/g, '').length < VALIDATION.MIN_LENGTH) {
            newErrors.purpose = VALIDATION.ERROR_MESSAGES.PURPOSE;
            isValid = false;
        }

        if (!promptData.prompt?.trim() || promptData.prompt.replace(/\s/g, '').length < VALIDATION.MIN_LENGTH) {
            newErrors.prompt = VALIDATION.ERROR_MESSAGES.PROMPT;
            isValid = false;
        }

        const customDataErrors = [];
        const customKeys = new Set(
            getDefaultDataCollection().map(field => field.key.toLowerCase())
        );

        promptData.customDataCollection?.forEach((field, index) => {
            const fieldErrors = {};
            const key = field.key?.trim();

            if (!key) {
                fieldErrors.key = VALIDATION.ERROR_MESSAGES.CUSTOM_KEY;
                isValid = false;
            } else if (!/^[A-Za-z0-9_]+$/.test(key)) {
                fieldErrors.key = VALIDATION.ERROR_MESSAGES.CUSTOM_KEY_FORMAT;
                isValid = false;
            } else if (customKeys.has(key.toLowerCase())) {
                fieldErrors.key = VALIDATION.ERROR_MESSAGES.CUSTOM_KEY_DUPLICATE;
                isValid = false;
            } else {
                customKeys.add(key.toLowerCase());
            }

            if (!field.type) {
                fieldErrors.type = VALIDATION.ERROR_MESSAGES.CUSTOM_TYPE;
                isValid = false;
            }

            if (!field.description?.trim() || field.description.replace(/\s/g, '').length < VALIDATION.MIN_LENGTH) {
                fieldErrors.description = VALIDATION.ERROR_MESSAGES.CUSTOM_DESCRIPTION;
                isValid = false;
            }

            if (Object.keys(fieldErrors).length) {
                customDataErrors[index] = fieldErrors;
            }
        });

        if (customDataErrors.length) {
            newErrors.customDataCollection = customDataErrors;
        }


        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm(selectedPrompt)) {
            return;
        }

        setLoading(true);
        try {
            const formData = {
                title: selectedPrompt.title,
                firstMessage: selectedPrompt.firstMessage,
                prompt: selectedPrompt.prompt,
                purpose: selectedPrompt.purpose,
                customDataCollection: (selectedPrompt.customDataCollection || [])
                    .filter(field => !isDefaultDataCollectionKey(field.key))
                    .map(field => ({
                        key: field.key.trim(),
                        type: field.type,
                        description: field.description.trim()
                    }))

            };

            const data = await updatePrompt(formData, selectedPrompt._id);
            if (data) {
                showToast("success", "Prompt Updated", "Your selected prompt has been updated successfully.");
                setUpdate(prev => prev + 1);
                setViewModal(false);
                setEditMode(false);
            }
        } catch (error) {
            showToast("error", "Update Failed", "Your prompt could not be updated.");
        } finally {
            setLoading(false);
        }
    };

    const deletePromptHandler = async () => {
        try {
            const data = await deletePrompt(selectedPrompt._id);
            if (data?.data?.prompt) {
                showToast("success", "Prompt Deleted", "Prompt has been deleted successfully.");
                setUpdate(prev => prev + 1);
            } else {
                showToast("error", "Delete Failed", data?.message || "Failed to delete prompt");
            }
        } catch (err) {
            showToast("error", "Delete Failed", "Prompt cannot be deleted");
        } finally {
            setDeleteModal(false);
        }
    };

    // Handler functions for better organization
    const handleViewPrompt = (prompt) => {
        setSelectedPrompt({
            ...prompt,
            customDataCollection: (prompt.customDataCollection || []).filter(
                field => !isDefaultDataCollectionKey(field.key)
            )
        });
        setViewModal(true);
        setEditMode(false);
        setErrors(emptyErrors);
    };

    const handleDeletePrompt = (prompt) => {
        setSelectedPrompt(prompt);
        setDeleteModal(true);
    };

    const handleEditToggle = () => {
        setEditMode(true);
        setErrors(emptyErrors);
    };

    const handleCloseModal = () => {
        setViewModal(false);
        setEditMode(false);
        setErrors(emptyErrors);
    };

    const handleCloseDeleteModal = () => {
        setDeleteModal(false);
    };

    const handleInputChange = (field, value) => {
        setSelectedPrompt(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleCustomDataChange = (index, field, value) => {
        const formattedValue = field === 'key'
            ? value.replace(/\s+/g, '_')
            : value;

        setSelectedPrompt(prev => ({
            ...prev,
            customDataCollection: (prev.customDataCollection || []).map((item, itemIndex) => (
                itemIndex === index ? { ...item, [field]: formattedValue } : item
            ))
        }));

        if (errors.customDataCollection?.[index]?.[field]) {
            setErrors(prev => {
                const customDataCollection = [...(prev.customDataCollection || [])];

                customDataCollection[index] = {
                    ...customDataCollection[index],
                    [field]: ""
                };

                return { ...prev, customDataCollection };
            });
        }
    };

    const handleAddCustomDataField = () => {
        setSelectedPrompt(prev => ({
            ...prev,
            customDataCollection: [
                ...(prev.customDataCollection || []),
                { key: "", type: "string", description: "" }
            ]
        }));
    };

    const handleDeleteCustomDataField = (index) => {
        setSelectedPrompt(prev => ({
            ...prev,
            customDataCollection: (prev.customDataCollection || []).filter((_, itemIndex) => itemIndex !== index)
        }));

        setErrors(prev => ({
            ...prev,
            customDataCollection: (prev.customDataCollection || []).filter((_, itemIndex) => itemIndex !== index)
        }));
    };


    // Helper function to truncate prompt text
    const truncatePrompt = (promptText) => {
        if (!promptText) return '';
        const cleanedText = promptText.replace(/\*\*|###|---/g, '');
        return `${cleanedText.split(" ").slice(0, 20).join(' ')} ...`;
    };

    // Render loading skeleton
    const renderSkeletonRows = () => {
        return [...Array(10)].map((_, index) => (
            <tr key={`skeleton-${index}`}>
                <td className="fs-12"><Skeleton height={12} width={30} /></td>
                <td className="fs-12"><Skeleton height={12} width={80} /></td>
                <td className="fs-12"><Skeleton height={12} width={80} /></td>
                <td className="fs-12"><Skeleton height={12} width={40} /></td>
            </tr>
        ));
    };

    // Render prompts rows
    const renderPromptRows = () => {
        if (prompts.length === 0) {
            return (
                <EmptyRow
                    colSpan={4}
                    icon="ri-text-block"
                    title="No prompt templates yet"
                    body="Create a template to reuse instructions across your agents."
                />
            );
        }

        return prompts.map((prompt, index) => (
            <tr key={prompt._id || index}>
                <td className="fs-12 fw-500">{index + 1}</td>
                <td className="fs-12 fw-500">{prompt.title}</td>
                <td className="fs-12 fw-500">{truncatePrompt(prompt.prompt)}</td>
                <td className="fs-20 fw-500 w-10">
                    <i
                        className="ri-eye-line cursor-pointer me-2"
                        onClick={() => handleViewPrompt(prompt)}
                        role="button"
                        aria-label="View prompt"
                    ></i>
                    <i
                        className="ri-delete-bin-line cursor-pointer"
                        onClick={() => handleDeletePrompt(prompt)}
                        role="button"
                        aria-label="Delete prompt"
                    ></i>
                </td>
            </tr>
        ));
    };

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="py-3 row">
                        <div className="col-12">
                            <Row className="my-2 align-items-center">
                                <Col md={8}>
                                    <h4>Prompt Management</h4>
                                    <p className='text-muted fs-12'>Manage your prompts</p>
                                </Col>
                                <Col md={4} className='text-end'>
                                    <Link
                                        id='create-prompt-btn'
                                        to={"/create-new-prompt"}
                                        className="font-normal py-2 px-4 rounded fs-16 text-decoration-none text-white linear-gradient-btn"
                                    >
                                        <i className="ri-add-line"></i> Create New Prompt
                                    </Link>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <Table hover responsive className='border-rounded custom-listing-tables'>
                                        <thead>
                                            <tr>
                                                <th>S No</th>
                                                <th>Title</th>
                                                <th>Prompt</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? renderSkeletonRows() : renderPromptRows()}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>

                            {/* Delete Confirmation Modal */}
                            <Modal isOpen={deleteModal} toggle={handleCloseDeleteModal} centered={true}>
                                <ModalHeader toggle={handleCloseDeleteModal}>
                                    <small>Delete Prompt</small>
                                </ModalHeader>
                                <ModalBody>
                                    <p className="text-secondary fw-light mb-0 font-size-12">
                                        Are you sure you want to delete this prompt?
                                    </p>
                                    <Row className="text-center mt-3">
                                        <Col>
                                            <Button
                                                color="danger"
                                                className="me-3"
                                                onClick={deletePromptHandler}
                                            >
                                                Confirm
                                            </Button>
                                            <Button
                                                outline
                                                color="primary"
                                                onClick={handleCloseDeleteModal}
                                            >
                                                Close
                                            </Button>
                                        </Col>
                                    </Row>
                                </ModalBody>
                            </Modal>

                            {/* View/Edit Prompt Modal */}
                            <Modal isOpen={viewModal} toggle={handleCloseModal} centered={true} size='lg'>
                                <ModalHeader toggle={handleCloseModal}>
                                    <small>Prompt Details</small>
                                </ModalHeader>
                                <ModalBody>
                                    {!editMode && (
                                        <p
                                            className='text-primary cursor-pointer text-end mb-0'
                                            onClick={handleEditToggle}
                                            role="button"
                                        >
                                            <i className="ri-edit-box-line"></i> Edit
                                        </p>
                                    )}

                                    <Row>
                                        <Col md={12} className='mb-4'>
                                            <Label htmlFor="prompt-title" className="text-left text-black font-bold">
                                                Prompt Title
                                            </Label>
                                            <input
                                                disabled={!editMode}
                                                id="prompt-title"
                                                className='custom-input form-control rounded border-0 py-2'
                                                type="text"
                                                value={selectedPrompt.title || ''}
                                                onChange={(e) => handleInputChange('title', e.target.value)}
                                            />
                                            {errors.title && <div className="text-danger small mt-1">{errors.title}</div>}
                                        </Col>

                                        <Col md={12} className='mb-4'>
                                            <Label htmlFor="first-message" className="text-left text-black font-bold">
                                                First Message
                                            </Label>
                                            <input
                                                disabled={!editMode}
                                                id="first-message"
                                                className='custom-input form-control rounded border-0 py-2'
                                                type="text"
                                                value={selectedPrompt.firstMessage || ''}
                                                onChange={(e) => handleInputChange('firstMessage', e.target.value)}
                                            />
                                            {errors.firstMessage && <div className="text-danger small mt-1">{errors.firstMessage}</div>}
                                        </Col>

                                        <Col md={12} className='mb-4'>
                                            <Label htmlFor="purpose" className="text-left text-black font-bold">
                                                Purpose
                                            </Label>
                                            <input
                                                disabled={!editMode}
                                                id="purpose"
                                                className='custom-input form-control rounded border-0 py-2'
                                                type="text"
                                                value={selectedPrompt.purpose || ''}
                                                onChange={(e) => handleInputChange('purpose', e.target.value)}
                                            />
                                            {errors.purpose && <div className="text-danger small mt-1">{errors.purpose}</div>}
                                        </Col>

                                        <Col lg={8} md={12} className='mb-4'>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Label htmlFor="prompt-content" className="text-left text-black font-bold mb-0">
                                                    Prompt
                                                </Label>
                                                <div className="d-flex gap-3">
                                                    {/*
                                                      Both read and write the saved prompt, so they
                                                      only make sense once there is one to act on.
                                                    */}
                                                    <VersionHistory
                                                        promptId={selectedPrompt._id}
                                                        currentPrompt={selectedPrompt.prompt}
                                                        onReverted={(updated) => {
                                                            if (updated?.prompt) handleInputChange('prompt', updated.prompt);
                                                        }}
                                                    />
                                                    <AskAiToChange
                                                        promptId={selectedPrompt._id}
                                                        onChanged={(updated) => {
                                                            if (updated?.prompt) handleInputChange('prompt', updated.prompt);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <textarea
                                                disabled={!editMode}
                                                id="prompt-content"
                                                value={selectedPrompt.prompt || ''}
                                                onChange={(e) => handleInputChange('prompt', e.target.value)}
                                                className='fs-16 rounded p-2 custom-textarea form-control'
                                                rows="14"
                                            ></textarea>
                                            {errors.prompt && <div className="text-danger small mt-1">{errors.prompt}</div>}
                                        </Col>
                                        <Col lg={4} md={12} className='mb-4'>
                                            <RubricPanel
                                                prompt={selectedPrompt.prompt}
                                                firstMessage={selectedPrompt.firstMessage}
                                                canTidy={editMode}
                                                onTidied={(sorted) => handleInputChange('prompt', sorted)}
                                            />
                                        </Col>

                                        <Col md={12} className='mb-4'>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Label className="text-left text-black font-bold mb-0">
                                                    Custom Data Collection
                                                </Label>

                                                {editMode && (
                                                    <Button
                                                        type="button"
                                                        onClick={handleAddCustomDataField}
                                                        className="px-3 py-2 rounded linear-gradient-btn"
                                                    >
                                                        Add More
                                                    </Button>
                                                )}
                                            </div>

                                            {(selectedPrompt.customDataCollection || []).length === 0 && !editMode && (
                                                <p className="text-muted small mb-3">No additional custom fields added.</p>
                                            )}

                                            {getDefaultDataCollection().map((field) => (
                                                <Row className="align-items-end mb-3" key={field.key}>
                                                    <Col md={3} className="mb-2">
                                                        <Label className="text-left text-black font-bold">Key</Label>
                                                        <input disabled className='custom-input form-control rounded border-0 py-2' value={field.key} />
                                                    </Col>

                                                    <Col md={3} className="mb-2">
                                                        <Label className="text-left text-black font-bold">Type</Label>
                                                        <input disabled className='custom-input form-control rounded border-0 py-2' value={field.type} />
                                                    </Col>

                                                    <Col md={5} className="mb-2">
                                                        <Label className="text-left text-black font-bold">Description</Label>
                                                        <input disabled className='custom-input form-control rounded border-0 py-2' value={field.description} />
                                                    </Col>

                                                    <Col md={1} className="mb-2">
                                                    </Col>
                                                </Row>
                                            ))}

                                            {(selectedPrompt.customDataCollection || []).map((field, index) => (
                                                <Row className="align-items-end mb-3" key={index}>
                                                    <Col md={3} className="mb-2">
                                                        <Label className="text-left text-black font-bold">
                                                            Key
                                                        </Label>
                                                        <input
                                                            disabled={!editMode}
                                                            className='custom-input form-control rounded border-0 py-2'
                                                            type="text"
                                                            placeholder="Key"
                                                            value={field.key || ''}
                                                            onChange={(e) => handleCustomDataChange(index, 'key', e.target.value)}
                                                        />
                                                        {errors.customDataCollection?.[index]?.key && (
                                                            <div className="text-danger small mt-1">{errors.customDataCollection[index].key}</div>
                                                        )}
                                                    </Col>

                                                    <Col md={3} className="mb-2">
                                                        <Label className="text-left text-black font-bold">
                                                            Type
                                                        </Label>
                                                        <select
                                                            disabled={!editMode}
                                                            className='custom-input form-control rounded border-0 py-2'
                                                            value={field.type || 'string'}
                                                            onChange={(e) => handleCustomDataChange(index, 'type', e.target.value)}
                                                        >
                                                            <option value="string">String</option>
                                                            <option value="boolean">Boolean</option>
                                                            <option value="integer">Integer</option>
                                                        </select>
                                                        {errors.customDataCollection?.[index]?.type && (
                                                            <div className="text-danger small mt-1">{errors.customDataCollection[index].type}</div>
                                                        )}
                                                    </Col>

                                                    <Col md={5} className="mb-2">
                                                        <Label className="text-left text-black font-bold">
                                                            Description
                                                        </Label>
                                                        <input
                                                            disabled={!editMode}
                                                            className='custom-input form-control rounded border-0 py-2'
                                                            type="text"
                                                            placeholder="Description"
                                                            value={field.description || ''}
                                                            onChange={(e) => handleCustomDataChange(index, 'description', e.target.value)}
                                                        />
                                                        {errors.customDataCollection?.[index]?.description && (
                                                            <div className="text-danger small mt-1">{errors.customDataCollection[index].description}</div>
                                                        )}
                                                    </Col>

                                                    {editMode && (
                                                        <Col md={1} className="mb-2">
                                                            <Button
                                                                type="button"
                                                                color="danger"
                                                                className="btn btn-sm rounded w-100 py-2"
                                                                onClick={() => handleDeleteCustomDataField(index)}
                                                                aria-label="Delete custom field"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>

                                                            </Button>
                                                        </Col>
                                                    )}
                                                </Row>
                                            ))}
                                        </Col>

                                    </Row>

                                    <Row className="text-center mt-3">
                                        <Col>
                                            {editMode && (
                                                <Button
                                                    className="me-3 linear-gradient-btn"
                                                    disabled={loading}
                                                    onClick={handleSubmit}
                                                >
                                                    {loading ? 'Updating...' : 'Update'}
                                                </Button>
                                            )}
                                            <Button
                                                outline
                                                color="primary"
                                                onClick={handleCloseModal}
                                            >
                                                Close
                                            </Button>
                                        </Col>
                                    </Row>
                                </ModalBody>
                            </Modal>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListPrompts;